import styled from "@emotion/styled";
import { map, sortBy } from "lodash-es";
import { observer } from "mobx-react-lite";
import { useMemo } from "react";

import { MDSIcon, MDSIconKind, MDSText, MDSTextVariant, MDSToggleButton } from "@/mds";
import { AgentActivityList } from "@/pages/agent/activities/agent-activity-list";
import { useAgentActivities } from "@/pages/agent/activities/use-agent-activities";
import {
  ALL_ACTIVITY_ENTITIES,
  ALL_ACTIVITY_PROJECTS,
  useAgentActivitiesSearchParams,
} from "@/pages/agent/activities/use-agent-activities-search-params";
import { useAgentOverlayAnalytics } from "@/pages/agent/analytics/use-agent-overlay-analytics";
import { AgentFilterSearchField } from "@/pages/agent/components/agent-filter-search-field";
import { AgentFilterSelect } from "@/pages/agent/components/agent-filter-select";
import type { AgentFilterSelectItem } from "@/pages/agent/components/agent-filter-select";
import { AgentPageFilterBar, AgentPageLayout } from "@/pages/agent/components/agent-page-layout";
import { AgentSurfaceNavigationActiveTab } from "@/pages/agent/components/agent-surface-navigation";
import { useAppStore } from "@/store";

const ACTIVITY_ENTITY_ITEMS: AgentFilterSelectItem[] = [
  {
    id: ALL_ACTIVITY_ENTITIES,
    label: "All entities",
    iconKind: MDSIconKind.Icons,
  },
  {
    id: "project",
    label: "Projects",
    iconKind: MDSIconKind.Folder,
  },
  {
    id: "task",
    label: "Tasks",
    iconKind: MDSIconKind.ListCheck,
  },
  {
    id: "routine",
    label: "Routines",
    iconKind: MDSIconKind.Calendar,
  },
];

export const AgentActivitiesPage = observer(() => {
  const { store } = useAppStore();
  const analytics = useAgentOverlayAnalytics();
  const { projectFilter, taskId, routineId, entityFilter, searchQuery, updateActivityFilters } =
    useAgentActivitiesSearchParams();
  const projectOptions = sortBy(
    store.assistantProjects.currentAssistantModels,
    project => project.title
  );

  const assistantRoutines = store.assistantRoutines.currentAssistantModels;
  const fixedRoutineIds = useMemo(
    () => new Set(assistantRoutines.filter(routine => routine.fixed).map(routine => routine.id)),
    [assistantRoutines]
  );

  const projectItems: AgentFilterSelectItem[] = [
    {
      id: ALL_ACTIVITY_PROJECTS,
      label: "All projects",
      iconKind: MDSIconKind.Folder,
    },
    ...map(projectOptions, project => ({
      id: project.id,
      label: project.title || "Untitled project",
      iconKind: MDSIconKind.Folder,
    })),
  ];
  if (
    projectFilter !== ALL_ACTIVITY_PROJECTS &&
    !projectItems.some(item => item.id === projectFilter)
  ) {
    projectItems.splice(1, 0, {
      id: projectFilter,
      label: "Selected project",
      iconKind: MDSIconKind.Folder,
    });
  }

  const task = taskId ? store.assistantTasks.getCurrentAssistantModelById(taskId) : undefined;
  const routine = routineId
    ? store.assistantRoutines.getCurrentAssistantModelById(routineId)
    : undefined;
  const activityQuery = useAgentActivities({
    query: searchQuery,
    projectId: projectFilter === ALL_ACTIVITY_PROJECTS ? null : projectFilter,
    taskId,
    routineId,
    entityKind: entityFilter === ALL_ACTIVITY_ENTITIES ? null : entityFilter,
    fixedRoutineIds,
    routinesResolved: store.assistantRoutines.currentAssistantModelsResolved,
  });

  return (
    <AgentPageLayout activeTab={AgentSurfaceNavigationActiveTab.Activities} fillsViewport>
      <AgentPageFilterBar>
        <StyledSortIndicator aria-label="Sorted by latest activity">
          <MDSIcon kind={MDSIconKind.ArrowUpArrowDown} size={14} />
          <MDSText variant={MDSTextVariant.TextS}>Latest activity</MDSText>
        </StyledSortIndicator>

        <AgentFilterSelect
          label="Project"
          selectedKey={projectFilter}
          defaultKey={ALL_ACTIVITY_PROJECTS}
          items={projectItems}
          leadingIcon={MDSIconKind.Folder}
          onSelectionChange={({ key }) => {
            analytics.trackFilterChange({ filterKind: "project", filterValue: key });
            updateActivityFilters({ nextProjectFilter: key });
          }}
          onClear={() => {
            analytics.trackFilterChange({
              filterKind: "project",
              filterValue: ALL_ACTIVITY_PROJECTS,
            });
            updateActivityFilters({ nextProjectFilter: ALL_ACTIVITY_PROJECTS });
          }}
        />

        <AgentFilterSelect
          label="Entity"
          selectedKey={entityFilter}
          defaultKey={ALL_ACTIVITY_ENTITIES}
          items={ACTIVITY_ENTITY_ITEMS}
          leadingIcon={MDSIconKind.Icons}
          onSelectionChange={({ key }) => {
            analytics.trackFilterChange({ filterKind: "entity", filterValue: key });
            updateActivityFilters({ nextEntityFilter: key });
          }}
          onClear={() => {
            analytics.trackFilterChange({
              filterKind: "entity",
              filterValue: ALL_ACTIVITY_ENTITIES,
            });
            updateActivityFilters({ nextEntityFilter: ALL_ACTIVITY_ENTITIES });
          }}
        />

        {taskId ? (
          <MDSToggleButton
            aria-label={`Task context: ${task?.title || "Task activity"}`}
            iconKind={MDSIconKind.ListCheck}
            isToggled
            label={task?.title || "Task activity"}
            onClear={() => {
              updateActivityFilters({ nextTaskId: null });
            }}
          />
        ) : null}

        {routineId ? (
          <MDSToggleButton
            aria-label={`Routine context: ${routine?.label || "Routine activity"}`}
            iconKind={MDSIconKind.Calendar}
            isToggled
            label={routine?.label || "Routine activity"}
            onClear={() => {
              updateActivityFilters({ nextRoutineId: null });
            }}
          />
        ) : null}

        <AgentFilterSearchField
          ariaLabel="Filter activity by name or project"
          placeholder="Filter by name or project"
          value={searchQuery}
          onChange={value => {
            const nextSearchQuery = value.slice(0, 200);
            analytics.trackFilterChange({
              filterKind: "search",
              queryLength: nextSearchQuery.trim().length,
            });
            updateActivityFilters({ nextSearchQuery });
          }}
        />
      </AgentPageFilterBar>

      <AgentActivityList
        activities={activityQuery.activities}
        hasActiveFilters={Boolean(
          projectFilter !== ALL_ACTIVITY_PROJECTS ||
          entityFilter !== ALL_ACTIVITY_ENTITIES ||
          taskId ||
          routineId ||
          searchQuery.trim()
        )}
        hasNextPage={activityQuery.hasNextPage}
        isError={activityQuery.isError}
        isFetchNextPageError={activityQuery.isFetchNextPageError}
        isFetchingNextPage={activityQuery.isFetchingNextPage}
        isLoading={activityQuery.isLoading}
        onLoadMore={() => {
          void activityQuery.fetchNextPage();
        }}
        onOpenCollection={({ collectionId }) => {
          analytics.trackItemClick({
            itemId: collectionId,
            itemKind: "collection",
            source: "activity_reference",
          });
          store.agentOverlay.close();
          store.navigation.goToCollection({ collectionId });
        }}
        onOpenNote={({ noteId }) => {
          analytics.trackItemClick({
            itemId: noteId,
            itemKind: "note",
            source: "activity_reference",
          });
          store.agentOverlay.close();
          store.navigation.goToNote({ noteId });
        }}
        onOpenFollowUp={({ followUpId }) => {
          analytics.trackItemClick({
            itemId: followUpId,
            itemKind: "follow_up",
            source: "activity_reference",
          });
          const followUp = store.assistantFollowUps.getCurrentAssistantModelById(followUpId);
          if (followUp?.taskId) {
            store.agentOverlay.openTask({ taskId: followUp.taskId });
          } else if (followUp?.projectId) {
            store.agentOverlay.openProject({ projectId: followUp.projectId });
          } else {
            store.agentOverlay.openTasks();
          }
        }}
        onOpenProject={({ projectId }) => {
          analytics.trackItemClick({
            itemId: projectId,
            itemKind: "project",
            source: "activity_reference",
          });
          store.agentOverlay.openProject({ projectId });
        }}
        onOpenRoutine={({ routineId: referencedRoutineId }) => {
          analytics.trackItemClick({
            itemId: referencedRoutineId,
            itemKind: "routine",
            source: "activity_reference",
          });
          store.agentOverlay.openRoutines();
        }}
        onOpenTask={({ taskId: referencedTaskId }) => {
          analytics.trackItemClick({
            itemId: referencedTaskId,
            itemKind: "task",
            source: "activity_reference",
          });
          store.agentOverlay.openTask({ taskId: referencedTaskId });
        }}
        onRetry={() => {
          void activityQuery.refetch();
        }}
      />
    </AgentPageLayout>
  );
});

const StyledSortIndicator = styled.div`
  align-items: center;
  background: ${({ theme }) => theme.colors.background.input.chrome.default};
  border-radius: 16px;
  color: ${({ theme }) => theme.colors.text.secondary};
  display: inline-flex;
  flex: 0 0 auto;
  gap: ${({ theme }) => theme.spacing[2]};
  height: 32px;
  padding: 0 ${({ theme }) => theme.spacing[3]};
`;
